import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  fetchFinancialGoals,
  updateFinancialGoal,
  fetchBusinessDomain,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmFinancialGoalEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  // Selected as array of indicatorName strings
  const [selectedNames, setSelectedNames] = useState([]);
  const [domainIndicators, setDomainIndicators] = useState([]);
  const [fullData, setFullData] = useState(null);
  // 因果関係：この目標に影響を与える財務目標のID一覧（自分自身は除外）
  const [sourceIds, setSourceIds] = useState([]);
  const [otherFinancialGoals, setOtherFinancialGoals] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [goals, domain] = await Promise.all([
          fetchFinancialGoals(businessDomainId),
          fetchBusinessDomain(businessDomainId),
        ]);
        const allGoals = Array.isArray(goals) ? goals : [];
        const found = allGoals.find(
          (g) => String(g.successFactorId) === String(successFactorId)
        );
        if (found) {
          setFullData(found);
          setSuccessFactorName(found.successFactorName || "");
          setSuccessFactorDescription(found.successFactorDescription || "");
          // Extract selected indicator names from existing data
          setSelectedNames((found.indicators || []).map((ind) => ind.indicatorName));
          // 既存の因果関係を復元
          setSourceIds((found.sources || []).map((s) => s.successFactorId));
        }
        // 自分自身を除いた他の財務目標
        setOtherFinancialGoals(allGoals.filter((g) => g.successFactorId !== successFactorId));
        setDomainIndicators(Array.isArray(domain?.indicators) ? domain.indicators : []);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 指標が選択されていない場合はエラー（サーバーが空配列で指標を消せないため必須）
    if (domainIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      // Map selected names back to full indicator objects
      const indicators = selectedNames
        .map((name) => domainIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);

      // 選択した因果関係を sources 形式に変換
      const sources = sourceIds
        .map((id) => otherFinancialGoals.find((g) => g.successFactorId === id))
        .filter(Boolean)
        .map((g) => ({ successFactorId: g.successFactorId, successFactorName: g.successFactorName }));

      await updateFinancialGoal({
        ...fullData,
        successFactorName,
        successFactorDescription,
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorId,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/strategy-map/financial-goals/${successFactorId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("financialGoal.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {/* 因果関係フィールド：影響を与える財務目標を選択（自分自身は除外） */}
          {otherFinancialGoals.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.sources.label")}</InputLabel>
                <Select
                  multiple
                  value={sourceIds}
                  onChange={(e) => setSourceIds(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.sources.label")} />}
                >
                  {otherFinancialGoals.map((g) => (
                    <MenuItem key={g.successFactorId} value={g.successFactorId}>
                      {g.successFactorName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {domainIndicators.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  value={selectedNames}
                  onChange={(e) => setSelectedNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {domainIndicators.map((ind) => (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                      {ind.indicatorName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              navigate(`/design/${businessDomainId}/strategy-map/financial-goals/${successFactorId}`)
            }
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
